import { StockSymbol } from '@benzinga/session';
import { AsOf, Financials, FinancialsQuery, FinancialsPeriod, Peer, ValuationRatiosQuery, Ownership } from './entities';

export class SecuritiesStore {
  private financials = new Map<
    StockSymbol,
    Map<FinancialsPeriod, Map<AsOf | undefined, { date: Date; financials: Financials[] }>>
  >();
  private fundamentals = new Map<
    StockSymbol,
    Map<FinancialsPeriod, Map<AsOf | undefined, { date: Date; fundamentals: Financials[] }>>
  >();
  private ownership = new Map<StockSymbol, { date: Date; ownership: Ownership }>();
  private peers = new Map<StockSymbol, { date: Date; peers: Peer[] }>();
  private valuationRatios = new Map<
    StockSymbol,
    Map<FinancialsPeriod, Map<AsOf | undefined, { date: Date; valuationRatios: Financials[] }>>
  >();
  private operationRatios = new Map<
    StockSymbol,
    Map<FinancialsPeriod, Map<AsOf | undefined, { date: Date; operationRatios: Financials[] }>>
  >();
  private earningReports = new Map<
    StockSymbol,
    Map<FinancialsPeriod, Map<AsOf | undefined, { date: Date; earningReports: Financials[] }>>
  >();

  public static compareFinancials = (lhs: Financials[], rhs: Financials[]): boolean => {
    return lhs?.every(financial => {
      const newFinancial = rhs.find(newFinancial => financial.id === newFinancial.id);
      if (newFinancial) {
        return financial.id === newFinancial.id;
      } else {
        return false;
      }
    });
  };

  public static compareOwnership = (lhs: Ownership, rhs: Ownership): boolean => {
    return lhs?.id === rhs?.id;
  };

  public static comparePeers = (lhs: Peer[], rhs: Peer[]): boolean => {
    return lhs?.every(peer => {
      const newPeer = rhs.find(newPeer => peer.symbol === newPeer.symbol);
      if (newPeer) {
        return peer.symbol === newPeer.symbol;
      } else {
        return false;
      }
    });
  };

  public static compareOperationRatios = (lhs: Financials[], rhs: Financials[]): boolean => {
    return lhs?.every(operationRatio => {
      const newOperationRatio = rhs.find(newOperationRatio => operationRatio.id === newOperationRatio.id);
      if (newOperationRatio) {
        return operationRatio.id === newOperationRatio.id;
      } else {
        return false;
      }
    });
  };

  public static compareValuationRatios = (lhs: Financials[], rhs: Financials[]): boolean => {
    return lhs?.every(valuationRatio => {
      const newValuationRatio = rhs.find(newValuationRatio => valuationRatio.id === newValuationRatio.id);
      if (newValuationRatio) {
        return valuationRatio.id === newValuationRatio.id;
      } else {
        return false;
      }
    });
  };

  public static compareEarningsReports = (lhs: Financials[], rhs: Financials[]): boolean => {
    return lhs?.every(earningsReport => {
      const newEarningsReport = rhs.find(newEarningsReport => earningsReport.id === newEarningsReport.id);
      if (newEarningsReport) {
        return earningsReport.id === newEarningsReport.id;
      } else {
        return false;
      }
    });
  };

  public getEarningReports = (query: ValuationRatiosQuery): Financials[] | undefined => {
    const ONE_MIN = 60 * 1000; /* ms */
    const earningReport = this.earningReports.get(query.symbols)?.get(query.period)?.get(query.asOf);
    const lastCalled = earningReport?.date.getTime() ?? 0;
    if (earningReport === undefined || Date.now() - lastCalled > ONE_MIN) {
      return undefined;
    }
    return earningReport.earningReports;
  };

  public setEarningReports = (query: ValuationRatiosQuery, earningReports: Financials[]): boolean => {
    const earningReport = this.earningReports.get(query.symbols)?.get(query.period)?.get(query.asOf);
    if (
      earningReport?.earningReports &&
      SecuritiesStore.compareEarningsReports(earningReport.earningReports, earningReports)
    ) {
      this.earningReports.get(query.symbols)?.get(query.period)?.set(query.asOf, { date: new Date(), earningReports });
      return true;
    } else {
      const val = this.earningReports.get(query.symbols);
      if (val) {
        const valFid = val.get(query.period);
        if (valFid) {
          valFid.set(query.asOf, { date: valFid.get(query.asOf)?.date ?? new Date(), earningReports });
        } else {
          val.set(query.period, new Map([[query.asOf, { date: new Date(), earningReports }]]));
        }
      } else {
        this.earningReports.set(
          query.symbols,
          new Map([[query.period, new Map([[query.asOf, { date: new Date(), earningReports }]])]]),
        );
      }
      return false;
    }
  };

  public getOperationRatios = (query: ValuationRatiosQuery): Financials[] | undefined => {
    const ONE_MIN = 60 * 1000; /* ms */
    const operationRatio = this.operationRatios.get(query.symbols)?.get(query.period)?.get(query.asOf);
    const lastCalled = operationRatio?.date.getTime() ?? 0;
    if (operationRatio === undefined || Date.now() - lastCalled > ONE_MIN) {
      return undefined;
    }
    return operationRatio.operationRatios;
  };

  public setOperationRatios = (query: ValuationRatiosQuery, operationRatios: Financials[]): boolean => {
    const operationRatio = this.operationRatios.get(query.symbols)?.get(query.period)?.get(query.asOf);
    if (
      operationRatio?.operationRatios &&
      SecuritiesStore.compareOperationRatios(operationRatio.operationRatios, operationRatios)
    ) {
      this.operationRatios
        .get(query.symbols)
        ?.get(query.period)
        ?.set(query.asOf, { date: new Date(), operationRatios });

      return true;
    } else {
      const val = this.operationRatios.get(query.symbols);
      if (val) {
        const valFid = val.get(query.period);
        if (valFid) {
          valFid.set(query.asOf, { date: valFid.get(query.asOf)?.date ?? new Date(), operationRatios });
        } else {
          val.set(query.period, new Map([[query.asOf, { date: new Date(), operationRatios }]]));
        }
      } else {
        this.operationRatios.set(
          query.symbols,
          new Map([[query.period, new Map([[query.asOf, { date: new Date(), operationRatios }]])]]),
        );
      }
      return false;
    }
  };

  public getValuationRatios = (query: ValuationRatiosQuery): Financials[] | undefined => {
    const ONE_MIN = 60 * 1000; /* ms */
    const valuationRatio = this.valuationRatios.get(query.symbols)?.get(query.period)?.get(query.asOf);
    const lastCalled = valuationRatio?.date.getTime() ?? 0;
    if (valuationRatio === undefined || Date.now() - lastCalled > ONE_MIN) {
      return undefined;
    }
    return valuationRatio.valuationRatios;
  };

  public setValuationRatios = (query: ValuationRatiosQuery, valuationRatios: Financials[]): boolean => {
    const valuationRatio = this.valuationRatios.get(query.symbols)?.get(query.period)?.get(query.asOf);
    if (
      valuationRatio?.valuationRatios &&
      SecuritiesStore.compareValuationRatios(valuationRatio.valuationRatios, valuationRatios)
    ) {
      this.valuationRatios
        .get(query.symbols)
        ?.get(query.period)
        ?.set(query.asOf, { date: new Date(), valuationRatios });
      return true;
    } else {
      const val = this.valuationRatios.get(query.symbols);
      if (val) {
        const valFid = val.get(query.period);
        if (valFid) {
          valFid.set(query.asOf, { date: valFid.get(query.asOf)?.date ?? new Date(), valuationRatios });
        } else {
          val.set(query.period, new Map([[query.asOf, { date: new Date(), valuationRatios }]]));
        }
      } else {
        this.valuationRatios.set(
          query.symbols,
          new Map([[query.period, new Map([[query.asOf, { date: new Date(), valuationRatios }]])]]),
        );
      }
      return false;
    }
  };

  public getFinancials = (query: FinancialsQuery): Financials[] | undefined => {
    const ONE_MIN = 60 * 1000; /* ms */
    const financial = this.financials.get(query.symbols)?.get(query.period)?.get(query.asOf);
    const lastCalled = financial?.date.getTime() ?? 0;
    if (financial === undefined || Date.now() - lastCalled > ONE_MIN) {
      return undefined;
    }
    return financial.financials;
  };

  public setFinancials = (query: FinancialsQuery, financials: Financials[]): boolean => {
    const financial = this.financials.get(query.symbols)?.get(query.period)?.get(query.asOf);
    if (financial?.financials && SecuritiesStore.compareFinancials(financial.financials, financials)) {
      this.financials.get(query.symbols)?.get(query.period)?.set(query.asOf, { date: new Date(), financials });
      return false;
    } else {
      const fin = this.financials.get(query.symbols);
      if (fin) {
        const periodFid = fin.get(query.period);
        if (periodFid) {
          periodFid.set(query.asOf, { date: periodFid.get(query.asOf)?.date ?? new Date(), financials });
        } else {
          fin.set(query.period, new Map([[query.asOf, { date: new Date(), financials }]]));
        }
      } else {
        this.financials.set(
          query.symbols,
          new Map([[query.period, new Map([[query.asOf, { date: new Date(), financials }]])]]),
        );
      }
      return true;
    }
  };

  public getFundamentals = (query: FinancialsQuery): Financials[] | undefined => {
    const ONE_MIN = 60 * 1000;
    const entry = this.fundamentals.get(query.symbols)?.get(query.period)?.get(query.asOf);
    const lastCalled = entry?.date.getTime() ?? 0;
    if (entry === undefined || Date.now() - lastCalled > ONE_MIN) {
      return undefined;
    }
    return entry.fundamentals;
  };

  public setFundamentals = (query: FinancialsQuery, fundamentals: Financials[]): boolean => {
    const entry = this.fundamentals.get(query.symbols)?.get(query.period)?.get(query.asOf);
    if (entry?.fundamentals && SecuritiesStore.compareFinancials(entry.fundamentals, fundamentals)) {
      this.fundamentals.get(query.symbols)?.get(query.period)?.set(query.asOf, { date: new Date(), fundamentals });
      return false;
    }
    const bySymbol = this.fundamentals.get(query.symbols);
    if (bySymbol) {
      const byPeriod = bySymbol.get(query.period);
      if (byPeriod) {
        byPeriod.set(query.asOf, { date: byPeriod.get(query.asOf)?.date ?? new Date(), fundamentals });
      } else {
        bySymbol.set(query.period, new Map([[query.asOf, { date: new Date(), fundamentals }]]));
      }
    } else {
      this.fundamentals.set(
        query.symbols,
        new Map([[query.period, new Map([[query.asOf, { date: new Date(), fundamentals }]])]]),
      );
    }
    return true;
  };

  public getOwnership = (symbol: string): Ownership | undefined => {
    const ONE_MIN = 60 * 1000; /* ms */
    const ownership = this.ownership.get(symbol);
    const lastCalled = ownership?.date.getTime() ?? 0;
    if (ownership === undefined || Date.now() - lastCalled > ONE_MIN) {
      return undefined;
    }
    return ownership.ownership;
  };

  public setOwnership = (symbol: string, ownership: Ownership): boolean => {
    const ownerships = this.ownership.get(symbol);
    if (ownerships?.ownership && SecuritiesStore.compareOwnership(ownerships.ownership, ownership)) {
      this.ownership.set(symbol, { ...ownerships, date: new Date() });
      return false;
    } else {
      this.ownership.set(symbol, { date: new Date(), ownership });
      return true;
    }
  };

  public getPeers = (symbol: string): Peer[] | undefined => {
    const ONE_MIN = 60 * 1000; /* ms */
    const peers = this.peers.get(symbol);
    const lastCalled = peers?.date.getTime() ?? 0;
    if (peers === undefined || Date.now() - lastCalled > ONE_MIN) {
      return undefined;
    }
    return peers.peers;
  };

  public setPeers = (symbol: string, peers: Peer[]): boolean => {
    const peer = this.peers.get(symbol);
    if (peer?.peers && SecuritiesStore.comparePeers(peer.peers, peers)) {
      this.peers.set(symbol, { ...peer, date: new Date() });
      return false;
    } else {
      this.peers.set(symbol, { date: new Date(), peers });
      return true;
    }
  };
}
